import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { getConversationRatingToolConfig, getSimplrAccountIdOfSelectedTicket } from '../../extractFromState';
import { useCallbackOnOutsideClick } from '../../customHooks';
import { handleError } from 'services/ErrorService/ErrorService';
import { oneBtnPopupCreator } from '../popups/ConfirmPopup/helper';
import { setLoading } from 'actions/index';
import { submitQualityRating } from 'api/rating';
import isEmpty from 'lodash/isEmpty';
import { getConversationRatingData } from 'selectors/conversationRatings';
import RatingDropdown from './RatingDropdown/RatingDropdown';
import { setRatingScore, setRatingScoreData } from 'actions/conversationRatingActions';
import { FEEDBACK_LABEL, FOLLOW_UP_LABEL, RATING_SCORES } from 'constants/ratingOptions';
import { RatingOption, StyledConversationRating } from './ConversationRating.styles';
import { SmallTextWhite } from '../Messages/SelectedMessage/SelectedMessage.styles';
import { getMessagesLoadingState } from '../../selectors/tools';
import { useTheme } from 'context/ThemeContext';

const qualityRatingCategoriesMap = {
  'What was wrong?': 'bad',
  'What was good?': 'good',
  'Follow up actions': 'follow_up_actions',
  'Add note': 'note',
};
const initialRatingData = { ratingScore: null };

const INITIAL_TIMER_SECONDS = 5;

const formatRatingScoreDetails = (ratingScoreDetails) => {
  let result = [];

  if (!isEmpty(ratingScoreDetails)) {
    result = Object.entries(ratingScoreDetails).map(([key, value]) => ({
      category: qualityRatingCategoriesMap[key],
      options: value,
    }));
  }

  return result;
};

const ConversationRating = ({
  ratingToolConfig = {},
  conversationId,
  expertsObject,
  userId,
  simplrAccountId,
  showBlockingSpinner,
  darkTheme: darkThemeProp,
  allMessages,
  setRatingScoreToStore,
  setRatingScoreDataToStore,
  ratingData = { ...initialRatingData },
  greyOutFeatureEnabled,
  isLoading,
}) => {
  const { isDarkMode } = useTheme();
  const darkTheme = darkThemeProp || isDarkMode;
  const formRef = useRef();
  // close dropdown if clicked outside
  useCallbackOnOutsideClick(formRef, () => setIsDropdownOpened(false));
  const [isDropdownOpened, setIsDropdownOpened] = useState(false);
  const [areButtonsDisabled, setAreButtonsDisabled] = useState(true);
  const [counterValue, setCounterValue] = useState(null);

  useEffect(() => {
    if (!greyOutFeatureEnabled) {
      setAreButtonsDisabled(false);

      return;
    }

    setAreButtonsDisabled(true);
    setCounterValue(INITIAL_TIMER_SECONDS);

    const counter = setInterval(() => {
      setCounterValue((prevCounterValue) => {
        if (prevCounterValue <= 1) {
          clearInterval(counter);
          setAreButtonsDisabled(false);

          return 0;
        }

        return prevCounterValue - 1;
      });
    }, 1000);

    return () => clearInterval(counter);
  }, [greyOutFeatureEnabled]);

  const isRatingScoreData = (ratingScore) => {
    return ratingData && ratingData[ratingScore];
  };

  const getRatingScoreDetails = (ratingScore) => {
    const ratingScoreDetails = isRatingScoreData(ratingScore) && ratingData[ratingScore].ratingScoreDetails;

    return ratingScoreDetails || {};
  };

  const getSelectedExpert = (ratingScore) => {
    const selectedExpert = isRatingScoreData(ratingScore) && ratingData[ratingScore].selectedExpert;

    return selectedExpert || {};
  };

  const getCurrentlyActiveNode = (ratingScore) => {
    const currentlyActiveNode = isRatingScoreData(ratingScore) && ratingData[ratingScore].currentlyActiveNode;

    return currentlyActiveNode || 0;
  };

  const updateRatingScore = (ratingScore) => {
    if (ratingScore !== ratingData.ratingScore) {
      setRatingScoreToStore(conversationId, ratingScore);
    }
    setIsDropdownOpened(true);
  };

  const setRatingScoreDetails = (ratingScoreDetails) => {
    const score = ratingData.ratingScore;
    const ratingDataForScore = score && ratingData[score];
    setRatingScoreDataToStore(conversationId, ratingData.ratingScore, { ...ratingDataForScore, ratingScoreDetails });
  };

  const setSelectedExpert = (selectedExpert) => {
    setRatingScoreDataToStore(conversationId, ratingData.ratingScore, { selectedExpert });
  };

  const setCurrentlyActiveNode = (currentlyActiveNode) => {
    const score = ratingData.ratingScore;
    const ratingDataForScore = score && ratingData[score];
    setRatingScoreDataToStore(conversationId, ratingData.ratingScore, { ...ratingDataForScore, currentlyActiveNode });
  };

  const isShareFeedbackSelected = (ratingScore) => {
    const ratingDetails = getRatingScoreDetails(ratingScore);
    const followUpActions = ratingDetails[FOLLOW_UP_LABEL];
    const shareFeedbackSelected = Array.isArray(followUpActions) && followUpActions.includes(FEEDBACK_LABEL);

    return shareFeedbackSelected;
  };

  const handleSubmitRating = async (e) => {
    e && e.preventDefault();

    try {
      setIsDropdownOpened(false);
      showBlockingSpinner(true, 'ConversationRating');

      let params = {
        conversationId,
        simplrAccountId,
        submittedById: userId,
        ratingDetails: formatRatingScoreDetails(getRatingScoreDetails(ratingData.ratingScore)),
        ratingScore: ratingData.ratingScore,
      };

      // If the rating is positive (thumbs up) but 'Share Feedback' was not selected (and there is at least one expert)
      // submit the Quality Rating for all experts that responded to ticket
      if (
        ratingData.ratingScore === 5 &&
        !isShareFeedbackSelected(ratingData.ratingScore) &&
        expertsObject.length >= 1
      ) {
        await Promise.all(
          expertsObject.map(async (expert) => {
            submitQualityRating({ ...params, reviewedExpertId: expert.value || null });
          })
        );
      } else {
        await submitQualityRating({
          ...params,
          reviewedExpertId: getSelectedExpert(ratingData.ratingScore).value || null,
        });
      }

      oneBtnPopupCreator({
        content: {
          main: 'Your rating was successfully submitted!',
        },
        btnConfig: 'CLOSE',
      });
    } catch (error) {
      handleError({ error, customMessage: 'There was a problem submitting the rating', showPopup: true });
    } finally {
      showBlockingSpinner(false, 'ConversationRating');
    }
  };

  if (isLoading) {
    return;
  }

  if (areButtonsDisabled) {
    return (
      <StyledConversationRating
        ref={formRef}
        onSubmit={(e) => handleSubmitRating(e)}
        darkTheme={darkTheme}
        $isDisabled={areButtonsDisabled}
        aria-expanded={isDropdownOpened}
      >
        <SmallTextWhite>{`in ${counterValue}s`}</SmallTextWhite>
      </StyledConversationRating>
    );
  }

  return (
    <StyledConversationRating
      ref={formRef}
      onSubmit={(e) => handleSubmitRating(e)}
      darkTheme={darkTheme}
      $isDisabled={areButtonsDisabled}
      aria-expanded={isDropdownOpened}
    >
      {RATING_SCORES.map((ratingScore, i) => (
        <RatingOption
          key={i}
          onClick={() => updateRatingScore(ratingScore)}
          value={ratingScore}
          previousRating={ratingData.ratingScore}
          darkTheme={darkTheme}
          isDisabled={areButtonsDisabled}
        />
      ))}
      {isDropdownOpened && (
        <RatingDropdown
          ratingToolConfig={ratingToolConfig}
          ratingScore={ratingData.ratingScore}
          expertsObject={expertsObject}
          allMessages={allMessages}
          currentlyActiveNode={getCurrentlyActiveNode(ratingData.ratingScore)}
          ratingScoreDetails={getRatingScoreDetails(ratingData.ratingScore)}
          selectedExpert={getSelectedExpert(ratingData.ratingScore)}
          setRatingScoreDetails={setRatingScoreDetails}
          setSelectedExpert={setSelectedExpert}
          setCurrentlyActiveNode={setCurrentlyActiveNode}
        />
      )}
    </StyledConversationRating>
  );
};

const mapStateToProps = (state) => ({
  simplrAccountId: getSimplrAccountIdOfSelectedTicket(state),
  ratingToolConfig: getConversationRatingToolConfig(state),
  conversationId: state.conversation.selectedConversationId,
  userId: state.user.id,
  allMessages: state.conversation,
  ratingData: getConversationRatingData(state),
  isLoading: getMessagesLoadingState(state),
});

const mapDispatchToProps = {
  setRatingScoreToStore: setRatingScore,
  setRatingScoreDataToStore: setRatingScoreData,
  showBlockingSpinner: setLoading,
};

export default connect(mapStateToProps, mapDispatchToProps)(ConversationRating);
